import mergeRefs from 'lib/helpers/misc';
import { ChangeEvent, CSSProperties, FC, useState } from 'react';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import MaskedInput from '@biproxi/react-text-mask';
import styled from 'styled-components';
import COLORS from 'lib/constants/colors';
import ErrorMessage from './ErrorMessage';
import { Flex } from './Flex';

interface Props {
  placeholder?: string;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  style?: CSSProperties;
  required?: boolean;
  value?: string | string[] | number;
  id?: string;
  label?: string;
  type?: string;
  name?: string;
  mask?: any[] | ((text: string) => any[]);
  readOnly?: boolean;
  disabled?: boolean;
  defaultValue?: string | string[];
  suffix?: string | React.ReactElement;
  preffix?: string | React.ReactElement;
  autoFocus?: boolean;
  inputRef?: React.Ref<HTMLInputElement>;
  errorMessage?: string;
  min?: string;
  max?: string;
  inputStyle?: React.CSSProperties;
  containerStyle?: React.CSSProperties;
  maxLength?: number;
  disabledAutoComplete?: boolean;
  autoComplete?: string;
}

const InputWrapper = styled.div`
  width: 100%;
  padding-left: 12px;
  padding-right: 4px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Input = styled.input`
  border-radius: 4px;
  font-size: 17px;
  background: transparent;
  border: none;
  width: 100%;
`;

interface ContainerProps {
  focused: boolean;
  disabled: boolean;
  error: boolean;
}

const Container = styled.div<ContainerProps>`
  border-radius: 12px;
  height: 48px;
  width: 100%;
  display: flex;
  align-items: center;
  border: 1px solid
    ${(props) => {
      if (props.error) return COLORS.pink;

      return props.focused ? COLORS.black : '#ababab';
    }};
  background: ${(props) => (props.disabled ? '#EBEBEB' : '#FFF')};
`;

const TextField: FC<Props> = (props) => {
  const {
    mask,
    onBlur = () => null,
    onFocus = () => null,
    readOnly = false,
    disabled = false,
    suffix,
    preffix,
    inputRef,
    errorMessage,
    inputStyle,
    containerStyle,
    disabledAutoComplete,
    autoComplete,
  } = props;

  const disableProps = disabledAutoComplete
    ? {
        autoComplete: autoComplete || 'off',
        autoCorrect: 'off',
        spellCheck: false,
      }
    : {};

  const [focused, setFocused] = useState(false);

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    setFocused(false);
    onBlur(event);
  };

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    setFocused(true);
    onFocus(event);
  };

  return (
    <div>
      <Container
        style={containerStyle}
        focused={focused}
        disabled={disabled}
        error={!!errorMessage}
      >
        <InputWrapper>
          {preffix && <Flex style={{ marginRight: 6 }}>{preffix}</Flex>}
          {mask ? (
            <MaskedInput
              {...props}
              {...disableProps}
              onFocus={handleFocus}
              onBlur={handleBlur}
              mask={mask}
              showMask={false}
              guide={false}
              style={inputStyle as any}
              render={(ref2: any, props2: any) => (
                <Input ref={mergeRefs([ref2, inputRef])} {...props2} />
              )}
              readOnly={readOnly}
              disabled={disabled}
            />
          ) : (
            <Input
              {...props}
              {...disableProps}
              style={inputStyle}
              onFocus={handleFocus}
              onBlur={handleBlur}
              readOnly={readOnly}
              disabled={disabled}
              ref={inputRef}
            />
          )}
          {suffix && suffix}
        </InputWrapper>
      </Container>
      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
    </div>
  );
};

export default TextField;
